import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { FormattedCurrencyInput } from "../formatted-currency-input";
import { DataTable, DataTableProps } from "./data-table";

import { SortDirection, TableColumn } from "@/types/table/tableProps";

export interface EditableDataTableRef<T> {
  getEditedData: () => T[];
}

export interface EditableDataTableProps<T> {
  // v1 compatibility props
  direction?: "asc" | "desc";
  itemsPerPage?: number;
  totalItems?: number;
  setPage?: (page: number) => void;
  setItemsPerPage?: (pageSize: number) => void;
  onSort?: (
    accessor: TableColumn<T>["accessor"],
    direction: SortDirection | null,
  ) => void; // v2 signature (also supports v1 via conversion)

  // Editing props - fully controlled
  editableFields?: Array<keyof T>;
  entityName?: string;
  isEditing?: boolean;
  isEditDisabled?: boolean;
  isEditMode: boolean; // Required - external control
  onEditModeChange: (isEditMode: boolean) => void; // Required - external control
  onUpdateData?: (newData: T[]) => Promise<void>;
  onSave?: () => void | Promise<void>; // Optional external save handler - parent should use ref to get editedData
  onCancel?: () => void; // Optional external cancel handler

  // v2 DataTable props (pass through)
  columns: TableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  orderBy?: string;
  descending?: boolean;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T, index: number) => string | number;
  emptyMessage?: string;
  emptyState?: DataTableProps<T>["emptyState"];
  page?: number;
  pageSize?: number;
  totalRecords?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

function EditableDataTableInner<T>(
  {
    // v1 compatibility props
    direction,
    itemsPerPage,
    totalItems,
    setPage,
    setItemsPerPage,
    onSort: onSortV1,

    // Editing props - fully controlled
    editableFields,
    entityName: _entityName = "Item",
    isEditing: _isEditing = false,
    isEditDisabled: _isEditDisabled = false,
    isEditMode, // Required - external control
    onEditModeChange: _onEditModeChange, // Required - external control
    onUpdateData: _onUpdateData,
    onSave: _onSave,
    onCancel: _onCancel,

    // v2 DataTable props
    columns,
    data,
    isLoading = false,
    orderBy,
    descending: descendingProp,
    onRowClick,
    getRowId,
    emptyMessage,
    emptyState,
    page: pageProp,
    pageSize: pageSizeProp,
    totalRecords: totalRecordsProp,
    onPageChange: onPageChangeProp,
    onPageSizeChange: onPageSizeChangeProp,
  }: EditableDataTableProps<T>,
  ref: React.Ref<EditableDataTableRef<T>>,
) {
  const [editedData, setEditedData] = useState<T[]>(data);

  // Update editedData when data changes
  useEffect(() => {
    setEditedData(data);
  }, [data]);

  // Reset editedData when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      setEditedData(data);
    }
  }, [isEditMode, data]);

  // Convert v1 direction to v2 descending
  const descending = useMemo(() => {
    if (descendingProp !== undefined) {
      return descendingProp;
    }
    if (direction === "desc") {
      return true;
    }
    if (direction === "asc") {
      return false;
    }
    return false;
  }, [descendingProp, direction]);

  // Use the sort handler directly (v2 signature)
  const handleV2Sort = onSortV1;

  // Map v1 props to v2 props
  const page = pageProp ?? 1;
  const pageSize = pageSizeProp ?? itemsPerPage ?? 25;
  const totalRecords = totalRecordsProp ?? totalItems ?? 0;
  const onPageChange = onPageChangeProp ?? setPage;
  const onPageSizeChange = onPageSizeChangeProp ?? setItemsPerPage;

  // Handle field changes in edit mode
  const handleFieldChange = useCallback(
    (rowIndex: number, field: keyof T, value: number | undefined) => {
      setEditedData((prevData) => {
        const newData = [...prevData];
        newData[rowIndex] = {
          ...newData[rowIndex],
          [field]: value ?? null,
        };
        return newData;
      });
    },
    [],
  );

  // Expose editedData via ref so parent can access it
  useImperativeHandle(ref, () => ({
    getEditedData: () => editedData,
  }));

  // Create columns with editable cell support
  const editableColumns = useMemo(() => {
    if (!isEditMode || !editableFields || editableFields.length === 0) {
      return columns;
    }

    return columns.map((column) => {
      const accessor = column.accessor as keyof T;
      const isEditable =
        accessor && editableFields.includes(accessor as keyof T);

      if (!isEditable) {
        return column;
      }

      // Wrap the Cell renderer to show CurrencyInput in edit mode
      return {
        ...column,
        Cell: (row: T, index: number) => {
          const rawValue = row[accessor];
          let numericValue: number | undefined;

          if (rawValue === null || rawValue === undefined) {
            numericValue = undefined;
          } else {
            const parsed = Number(rawValue);
            numericValue = Number.isNaN(parsed) ? undefined : parsed;
          }

          const isLastColumn = columns.indexOf(column) === columns.length - 1;
          const shouldAlignRight = isLastColumn || column.textAlign === "right";
          const MAX_BUDGET_VALUE = 9999999.99; // 7 digits + 2 decimals
          return (
            <div className={shouldAlignRight ? "flex justify-end w-full" : ""}>
              <FormattedCurrencyInput
                value={numericValue}
                maxValue={MAX_BUDGET_VALUE}
                decimals
                onChange={(value) => {
                  handleFieldChange(index, accessor, value);
                }}
              />
            </div>
          );
        },
      };
    });
  }, [columns, isEditMode, editableFields, handleFieldChange]);

  // Use edited data in edit mode, otherwise use original data
  const displayData = isEditMode ? editedData : data;

  // Disable row clicks in edit mode
  const handleRowClick = isEditMode ? undefined : onRowClick;

  return (
    <DataTable<T>
      columns={editableColumns}
      data={displayData}
      isLoading={isLoading}
      onRowClick={handleRowClick}
      onSort={handleV2Sort}
      orderBy={orderBy}
      descending={descending}
      page={page}
      pageSize={pageSize}
      totalRecords={totalRecords}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      getRowId={getRowId}
      emptyMessage={emptyMessage}
      emptyState={emptyState}
    />
  );
}

export const EditableDataTable = forwardRef(EditableDataTableInner) as <T>(
  props: EditableDataTableProps<T> & {
    ref?: React.Ref<EditableDataTableRef<T>>;
  },
) => JSX.Element;
